/* eslint-disable react/require-default-props, react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import {
  X,
  AlertTriangle,
  User,
  BookOpen,
  FileText,
  ExternalLink,
  CheckCircle,
  XCircle,
  Video,
  Link as LinkIcon,
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import { cn } from '../../lib/utils';
import NotificationsStatusEnum from '../../pages/Home/enums/notifications-status.enum';

const materialIcons = {
  video: Video,
  article: FileText,
  book: BookOpen,
  link: LinkIcon,
  default: FileText,
};

function NotificationDetailModal({ isOpen, onClose, notification, user }) {
  if (!isOpen) return null;

  const handleSetStatus = (status) => {
    onClose(status);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getMaterialIcon = (type) => {
    const IconComponent = materialIcons[type?.toLowerCase()] || materialIcons.default;
    return IconComponent;
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
      role="button"
      tabIndex={0}
    >
      <div className="relative w-full max-w-lg mx-4 rounded-lg border bg-background shadow-xl animate-in fade-in-0 zoom-in-95">
        {/* Close button */}
        <button
          type="button"
          className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity"
          onClick={() => onClose()}
        >
          <X className="h-4 w-4" />
        </button>

        {/* Header */}
        <div className="flex flex-col items-center gap-3 p-6 pb-4 border-b">
          <div className="rounded-full bg-destructive/10 p-3">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <div className="text-center">
            <Badge variant="destructive" className="mb-2">
              {notification.type}
            </Badge>
            <h2 className="text-lg font-semibold">{notification.details.detailName}</h2>
          </div>
        </div>

        {/* Content */}
        <ScrollArea className="max-h-[60vh]">
          <div className="p-6 space-y-4">
            {/* Sent by */}
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-muted p-2 shrink-0">
                <User className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Sent by</p>
                <p className="font-medium">
                  {user.firstName} {user.lastName}
                </p>
              </div>
            </div>

            {/* Skill name */}
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-muted p-2 shrink-0">
                <BookOpen className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Skill</p>
                <p className="font-medium">{notification.details.parentName}</p>
              </div>
            </div>

            {/* Materials */}
            <div>
              <p className="text-sm text-muted-foreground mb-2">Reported Materials</p>
              <div className="space-y-2">
                {notification.details.materialIds.map((materialId, index) => {
                  const IconComponent = getMaterialIcon(
                    notification.details.materialTypes?.[index],
                  );
                  const link = notification.details.links?.[index];
                  const name = notification.details.materialNames?.[index] || 'Unknown material';

                  return (
                    <div
                      key={materialId}
                      className="flex items-center gap-2 p-2 rounded-md border bg-muted/30"
                    >
                      <IconComponent className="h-4 w-4 text-muted-foreground shrink-0" />
                      {link ? (
                        <a
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 text-sm text-primary hover:underline flex items-center gap-1"
                        >
                          {name}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span className="flex-1 text-sm">{name}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Description */}
            <div>
              <p className="text-sm text-muted-foreground mb-2">Description</p>
              <div className="p-3 rounded-md border bg-muted/30">
                <p className="text-sm whitespace-pre-wrap">{notification.details.description}</p>
              </div>
            </div>
          </div>
        </ScrollArea>

        {/* Actions */}
        <div className="flex gap-3 p-6 pt-4 border-t">
          <Button
            variant="outline"
            className={cn(
              'flex-1 gap-2',
              'hover:bg-destructive/10 hover:text-destructive hover:border-destructive',
            )}
            onClick={() => handleSetStatus(NotificationsStatusEnum.DISMISSED)}
          >
            <XCircle className="h-4 w-4" />
            Dismiss
          </Button>
          <Button
            className="flex-1 gap-2 bg-green-600 hover:bg-green-700"
            onClick={() => handleSetStatus(NotificationsStatusEnum.FIXED)}
          >
            <CheckCircle className="h-4 w-4" />
            Mark as Fixed
          </Button>
        </div>
      </div>
    </div>
  );
}

NotificationDetailModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  notification: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    details: PropTypes.shape({
      materialIds: PropTypes.arrayOf(PropTypes.string).isRequired,
      materialTypes: PropTypes.arrayOf(PropTypes.string),
      links: PropTypes.arrayOf(PropTypes.string),
      parentId: PropTypes.string.isRequired,
      parentName: PropTypes.string.isRequired,
      detailName: PropTypes.string.isRequired,
      materialNames: PropTypes.arrayOf(PropTypes.string).isRequired,
      description: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  user: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }).isRequired,
};

export default NotificationDetailModal;
